import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';
import { Barbell, Plus, PencilSimple, Trash, Clock, CalendarBlank } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';

const AdminTrainers = () => {
  const { language, API, user } = useAuth();
  const L = (en, deu) => language === 'de' ? deu : en;
  const isAdmin = user?.role === 'admin';

  const [trainers, setTrainers] = useState([]);
  const [trainerStats, setTrainerStats] = useState({}); // { trainerId: { week, month, range } }
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', is_active: true });
  const [confirmAction, setConfirmAction] = useState(null);

  // Custom range picker — defaults to "last 30 days".
  const [rangeStart, setRangeStart] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [rangeEnd, setRangeEnd] = useState(format(new Date(), 'yyyy-MM-dd'));

  const loadTrainers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/trainers');
      setTrainers(data);
      // Week Mon–Sun of today, calendar month, plus the admin's custom range.
      const today = new Date();
      const wkStart = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const wkEnd = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
      const moStart = format(startOfMonth(today), 'yyyy-MM-dd');
      const moEnd = format(endOfMonth(today), 'yyyy-MM-dd');
      const fetchOne = (id, s, e) => API.get(`/trainers/${id}/stats?start_date=${s}&end_date=${e}`)
        .then(r => r.data).catch(() => ({ total_lessons: 0, total_hours: 0 }));
      const results = await Promise.all(data.map(async t => {
        const [week, month, range] = await Promise.all([
          fetchOne(t.id, wkStart, wkEnd),
          fetchOne(t.id, moStart, moEnd),
          fetchOne(t.id, rangeStart, rangeEnd),
        ]);
        return [t.id, { week, month, range }];
      }));
      setTrainerStats(Object.fromEntries(results));
    } catch (e) { console.error('Failed to load trainers:', e); }
    finally { setLoading(false); }
  }, [API, rangeStart, rangeEnd]);

  useEffect(() => { loadTrainers(); }, [loadTrainers]);

  const resetForm = () => setForm({ name: '', email: '', phone: '', is_active: true });

  const handleSave = async () => {
    try {
      if (editingTrainer) {
        await API.put(`/trainers/${editingTrainer.id}`, form);
        toast.success(L('Trainer updated!', 'Trainer aktualisiert!'));
      } else {
        await API.post('/trainers', form);
        toast.success(L('Trainer added!', 'Trainer hinzugefügt!'));
      }
      setShowModal(false); setEditingTrainer(null); resetForm();
    } catch (e) {
      const msg = e.response?.data?.detail || L('Error saving trainer', 'Fehler beim Speichern');
      toast.error(typeof msg === 'string' ? msg : L('Error', 'Fehler'));
      return;
    }
    // Reload separately so save errors don't get confused with load errors
    loadTrainers();
  };

  const handleEdit = (trainer) => {
    setEditingTrainer(trainer);
    setForm({ name: trainer.name, email: trainer.email || '', phone: trainer.phone || '', is_active: trainer.is_active });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    setConfirmAction({
      message: L('Delete this trainer permanently?', 'Diesen Trainer endgültig löschen?'),
      onConfirm: async () => {
        try {
          await API.delete(`/trainers/${id}`);
          toast.success(L('Deleted', 'Gelöscht'));
        } catch (e) {
          toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
        }
        setConfirmAction(null);
        loadTrainers();
      }
    });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-trainers">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-1">{L('Trainers', 'Trainer')}</h1>
          <p className="text-slate-500">{L('Manage coaches and track their performance', 'Trainer verwalten und Leistung verfolgen')}</p>
        </div>
        {isAdmin && (
          <Button onClick={() => { resetForm(); setEditingTrainer(null); setShowModal(true); }} className="btn-primary flex items-center gap-2" data-testid="add-trainer-btn">
            <Plus size={18} weight="bold" /> {L('Add Trainer', 'Trainer hinzufügen')}
          </Button>
        )}
      </div>

      {/* Custom time-range picker — feeds the third stat card on each trainer. */}
      <div className="card-static p-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center gap-2 text-slate-600 text-sm font-semibold">
          <CalendarBlank size={18} className="text-blue-500" />
          {L('Custom range for hours', 'Zeitraum für Stunden')}:
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)}
            className="bg-slate-50 border-slate-200 h-9 w-auto" data-testid="range-start" />
          <span className="text-slate-400">→</span>
          <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)}
            className="bg-slate-50 border-slate-200 h-9 w-auto" data-testid="range-end" />
        </div>
        <div className="flex items-center gap-1.5 ml-auto flex-wrap">
          {[
            { label: L('7d', '7T'), days: 7 },
            { label: L('30d', '30T'), days: 30 },
            { label: L('90d', '90T'), days: 90 },
            { label: L('YTD', 'YTD'), ytd: true },
          ].map(preset => (
            <button
              key={preset.label}
              type="button"
              onClick={() => {
                if (preset.ytd) {
                  setRangeStart(format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'));
                } else {
                  setRangeStart(format(subDays(new Date(), preset.days), 'yyyy-MM-dd'));
                }
                setRangeEnd(format(new Date(), 'yyyy-MM-dd'));
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition"
              data-testid={`range-preset-${preset.label}`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <div key={i} className="h-48 bg-slate-100 animate-pulse rounded-2xl" />)}
        </div>
      ) : trainers.length === 0 ? (
        <div className="card-static p-12 text-center">
          <Barbell size={48} className="text-slate-300 mx-auto mb-4" weight="duotone" />
          <p className="text-slate-500 mb-4">{isAdmin ? L('No trainers yet. Add your first trainer.', 'Noch keine Trainer. Fuegen Sie den ersten Trainer hinzu.') : L('No trainers yet.', 'Noch keine Trainer.')}</p>
          {isAdmin && <Button onClick={() => setShowModal(true)} className="btn-primary">{L('Add First Trainer', 'Ersten Trainer hinzufügen')}</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {trainers.map(trainer => {
            const stats = trainerStats[trainer.id] || {
              week:  { total_lessons: 0, total_hours: 0 },
              month: { total_lessons: 0, total_hours: 0 },
              range: { total_lessons: 0, total_hours: 0 },
            };
            return (
              <div key={trainer.id} className="card-static p-6" data-testid={`trainer-card-${trainer.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-purple-50 border border-purple-200 rounded-xl flex items-center justify-center">
                      <span className="text-purple-600 font-bold text-lg">{trainer.name.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-slate-900 font-bold">{trainer.name}</p>
                      {trainer.email && <p className="text-slate-400 text-xs">{trainer.email}</p>}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-bold rounded-full ${trainer.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                    {trainer.is_active ? L('Active', 'Aktiv') : L('Inactive', 'Inaktiv')}
                  </span>
                </div>

                {/* Hours breakdown — three time windows */}
                <div className="grid grid-cols-3 gap-3 mb-2">
                  <div className="bg-slate-50 p-3 rounded-xl text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{L('This week', 'Diese Woche')}</p>
                    <p className="text-2xl font-extrabold text-blue-600 leading-none" data-testid={`trainer-week-hours-${trainer.id}`}>
                      {(stats.week.total_hours || 0).toFixed(1)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{L('hours', 'Std.')} · {stats.week.total_lessons} L.</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-xl text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{L('This month', 'Dieser Monat')}</p>
                    <p className="text-2xl font-extrabold text-emerald-600 leading-none" data-testid={`trainer-month-hours-${trainer.id}`}>
                      {(stats.month.total_hours || 0).toFixed(1)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{L('hours', 'Std.')} · {stats.month.total_lessons} L.</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-xl text-center">
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{L('Range', 'Zeitraum')}</p>
                    <p className="text-2xl font-extrabold text-purple-600 leading-none" data-testid={`trainer-range-hours-${trainer.id}`}>
                      {(stats.range.total_hours || 0).toFixed(1)}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{L('hours', 'Std.')} · {stats.range.total_lessons} L.</p>
                  </div>
                </div>

                {/* Breakdown per booking kind for the range window — makes it
                    obvious where the "range" hours come from so the admin can
                    reconcile with the calendar. */}
                {(stats.range.individual_hours != null || stats.range.group_hours != null || stats.range.junior_hours != null) && (
                  <div className="flex flex-wrap gap-1.5 text-[10px] mb-4">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold" data-testid={`trainer-range-individual-${trainer.id}`}>
                      {L('Individual', 'Einzel')}: {(stats.range.individual_hours || 0).toFixed(1)}h
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold" data-testid={`trainer-range-group-${trainer.id}`}>
                      {L('Group', 'Gruppe')}: {(stats.range.group_hours || 0).toFixed(1)}h
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 font-semibold" data-testid={`trainer-range-junior-${trainer.id}`}>
                      {L('Junior', 'Junior')}: {(stats.range.junior_hours || 0).toFixed(1)}h
                    </span>
                  </div>
                )}

                {trainer.phone && <p className="text-slate-500 text-sm mb-4">{trainer.phone}</p>}

                {isAdmin && (
                  <div className="flex gap-2 border-t border-slate-100 pt-4">
                    <Button variant="outline" onClick={() => handleEdit(trainer)} className="flex-1 border-slate-200 text-slate-600 text-sm" data-testid={`edit-trainer-${trainer.id}`}>
                      <PencilSimple size={14} className="mr-1" /> {L('Edit', 'Bearbeiten')}
                    </Button>
                    <Button variant="outline" onClick={() => handleDelete(trainer.id)} className="border-red-200 text-red-500 hover:bg-red-50 text-sm" data-testid={`delete-trainer-${trainer.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Trainer Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-md" data-testid="trainer-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">{editingTrainer ? L('Edit Trainer', 'Trainer bearbeiten') : L('Add Trainer', 'Trainer hinzufügen')}</DialogTitle>
            <DialogDescription className="text-slate-500">{L('Manage trainer details', 'Trainerdaten verwalten')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="label-text">{L('Name', 'Name')} *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder="Max Mustermann" data-testid="trainer-name" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder="trainer@padelarena.ch" data-testid="trainer-email" />
            </div>
            <div className="space-y-2">
              <Label className="label-text">{L('Phone', 'Telefon')}</Label>
              <Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
                className="bg-slate-50 border-slate-200" placeholder="+41 79 ..." data-testid="trainer-phone" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="label-text">{L('Active', 'Aktiv')}</Label>
              <button onClick={() => setForm({...form, is_active: !form.is_active})}
                className={`w-12 h-6 rounded-full transition-colors ${form.is_active ? 'bg-emerald-500' : 'bg-slate-200'}`} data-testid="trainer-active-toggle">
                <div className={`w-5 h-5 bg-white rounded-full transition-transform shadow-sm ${form.is_active ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
              <Button onClick={handleSave} disabled={!form.name} className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 font-bold" data-testid="save-trainer-btn">
                {L('Save', 'Speichern')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold">{L('Confirm', 'Bestätigen')}</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600 mt-2">{confirmAction?.message}</p>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={() => setConfirmAction(null)} className="flex-1 border-slate-200 text-slate-600">{L('Cancel', 'Abbrechen')}</Button>
            <Button onClick={() => confirmAction?.onConfirm()} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold" data-testid="confirm-delete-trainer">
              {L('Delete', 'Löschen')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTrainers;
